/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useRef } from "react";
import { BEM, useTranslate } from "@adapptio/lib-components";
import { isInspectorNodeDefault, TInspectorNode } from "../../InspectorNodeTypes";
import {
	FieldListItem,
	FieldListItemAction,
	FieldListItemContent,
	FieldListItemHeader,
	Icon,
	Typography
} from "@adapptio/lib-editor-ui";
import { InspectorNode } from "../InspectorNode";
import { isFunction } from "@adapptio/lib-shared";

import "./InspectorNodeListItem.scss";
import { InspectorControl } from "../../controls";

export interface IInspectorNodeListItemProps {
	node: TInspectorNode;
	onExpand?: (node: TInspectorNode, elRef: React.MutableRefObject<HTMLDivElement>) => void;
	className?: string;
	expanded?: boolean;
	index: number;
	onDelete?: (index: number) => void;
	draggable?: boolean;
}

export const InspectorNodeListItem: React.FC<IInspectorNodeListItemProps> = ({
	className,
	node,
	onExpand,
	expanded,
	index,
	onDelete,
	draggable
}) => {
	const baseClass = BEM("edt-inspector-node-list-item", null, {
		expanded
	});

	const listItemRef = useRef<HTMLDivElement>();

	const t = useTranslate();

	const handleExpand = useCallback(() => {
		if (isFunction(onExpand)) {
			onExpand(node, listItemRef);
		}
	}, [ onExpand, node ]);

	const handleDelete = useCallback(() => {
		if (isFunction(onDelete)) {
			onDelete(index);
		}
	}, [ index, onDelete ]);

	if (className) {
		baseClass.add(className);
	}

	return (
		<FieldListItem
			index={index}
			expanded={expanded}
			onExpandChange={handleExpand}
			className={baseClass.toClassName()}
			draggable={draggable}
			ref={listItemRef}
		>
			<FieldListItemHeader
				className={baseClass.element("header").toClassName()}
				endAdornment={
					<>
						{node.controls.map((control) => (
							<InspectorControl key={control.key} control={control} />
						))}
					</>
				}
				secondaryAction={
					<FieldListItemAction onClick={handleDelete}>
						<Icon name="mdi/delete" />
					</FieldListItemAction>
				}
			>
				<Icon name="mdi/chevron-left" className={baseClass.element("expand-icon").toClassName()} />
				<Typography truncate>{t("editor", node.label)}</Typography>
			</FieldListItemHeader>
			<FieldListItemContent className={baseClass.element("content").toClassName()}>
				{isInspectorNodeDefault(node) &&
					node.children.map((resolveChild) => {
						const resolvedChild = resolveChild();
						return <InspectorNode key={resolvedChild.key} getNode={resolveChild} depth={1} />;
					})}
			</FieldListItemContent>
		</FieldListItem>
	);
};
